import { useState } from "react";

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      alert("Message must be 500 characters or fewer.");
      return;
    }

    setFormStatus("sending");

    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (input.name && input.value) {
        data.append(input.name, input.value);
      }
    });

    try {
      await fetch("https://readdy.ai/api/form/d7fmrqkq2rhf4jj5qong", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setFormStatus("success");
      form.reset();
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section id="contact" className="w-full bg-[#f7f4ef] py-24 md:py-36 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        {/* Top label */}
        <p className="text-stone-400 text-xs tracking-[0.25em] uppercase font-light mb-16">
          / Contact
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: Info */}
          <div className="flex flex-col gap-10">
            <div>
              <h2
                className="font-light text-stone-800 leading-snug mb-4"
                style={{ fontSize: "clamp(1.8rem, 3vw, 2.8rem)" }}
              >
                Get in Touch
              </h2>
              <p className="text-stone-400 font-light leading-relaxed text-base">
                Have a question about our iOS applications? We&apos;re here to help.
              </p>
            </div>

            {/* Contact details */}
            <div className="flex flex-col gap-6">
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-200 shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-stone-600 text-sm" />
                </div>
                <div>
                  <p className="text-stone-400 text-xs tracking-widest uppercase font-light mb-1">Address</p>
                  <p className="text-stone-700 text-sm font-light leading-relaxed">
                    8 MARTA BR. 52
                  </p>
                </div>
              </div>

              {/* City & Zip */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-200 shrink-0 mt-0.5">
                  <i className="ri-building-2-line text-stone-600 text-sm" />
                </div>
                <div>
                  <p className="text-stone-400 text-xs tracking-widest uppercase font-light mb-1">City &amp; Zip</p>
                  <p className="text-stone-700 text-sm font-light">
                    City: PODGORICA
                  </p>
                  <p className="text-stone-700 text-sm font-light">
                    Zip: 81000
                  </p>
                </div>
              </div>

              {/* Country */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-200 shrink-0 mt-0.5">
                  <i className="ri-global-line text-stone-600 text-sm" />
                </div>
                <div>
                  <p className="text-stone-400 text-xs tracking-widest uppercase font-light mb-1">Country</p>
                  <p className="text-stone-700 text-sm font-light">Montenegro</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-200 shrink-0 mt-0.5">
                  <i className="ri-mail-line text-stone-600 text-sm" />
                </div>
                <div>
                  <p className="text-stone-400 text-xs tracking-widest uppercase font-light mb-1">Email</p>
                  <p className="text-stone-700 text-sm font-light">help@dssdoo.com</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-200 shrink-0 mt-0.5">
                  <i className="ri-phone-line text-stone-600 text-sm" />
                </div>
                <div>
                  <p className="text-stone-400 text-xs tracking-widest uppercase font-light mb-1">Number</p>
                  <p className="text-stone-700 text-sm font-light">+1 5076235886</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div>
            <div className="bg-white rounded-2xl p-8 md:p-10">
              <h3 className="text-stone-800 font-medium text-lg mb-6">Send a Message</h3>

              {formStatus === "success" ? (
                <div className="flex flex-col items-center justify-center gap-4 py-12">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-stone-100">
                    <i className="ri-check-line text-stone-700 text-2xl" />
                  </div>
                  <p className="text-stone-700 font-medium text-base">Message sent!</p>
                  <p className="text-stone-400 text-sm text-center font-light">
                    Thank you for reaching out. We&apos;ll get back to you shortly.
                  </p>
                  <button
                    onClick={() => setFormStatus("idle")}
                    className="mt-2 text-xs text-stone-400 underline cursor-pointer whitespace-nowrap"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-stone-400 text-xs tracking-widest uppercase font-light">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Your name"
                        className="w-full border border-stone-200 rounded-md px-4 py-3 text-sm text-stone-700 bg-stone-50 focus:outline-none focus:border-stone-400 transition-colors font-light"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-stone-400 text-xs tracking-widest uppercase font-light">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="you@example.com"
                        className="w-full border border-stone-200 rounded-md px-4 py-3 text-sm text-stone-700 bg-stone-50 focus:outline-none focus:border-stone-400 transition-colors font-light"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-stone-400 text-xs tracking-widest uppercase font-light">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      placeholder="How can we help?"
                      className="w-full border border-stone-200 rounded-md px-4 py-3 text-sm text-stone-700 bg-stone-50 focus:outline-none focus:border-stone-400 transition-colors font-light"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-stone-400 text-xs tracking-widest uppercase font-light">
                      Message
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Your message (max 500 characters)"
                      className="w-full border border-stone-200 rounded-md px-4 py-3 text-sm text-stone-700 bg-stone-50 focus:outline-none focus:border-stone-400 transition-colors font-light resize-none"
                    />
                  </div>

                  {formStatus === "error" && (
                    <p className="text-red-500 text-xs">Something went wrong. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === "sending"}
                    className="w-full bg-stone-900 text-white text-sm font-medium tracking-widest uppercase rounded-md py-3.5 hover:bg-stone-700 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === "sending" ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Footer strip */}
        <div className="mt-24 border-t border-stone-200 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-stone-900 flex items-center justify-center">
              <span className="text-white text-xs font-bold">D</span>
            </div>
            <span className="text-stone-700 font-medium text-sm">DSS DOO</span>
          </div>
          <p className="text-stone-400 text-xs font-light">
            &copy; {new Date().getFullYear()} DSS DOO. All rights reserved.
          </p>
          <p className="text-stone-300 text-xs font-light tracking-wide">
            iOS Application Development
          </p>
        </div>
      </div>
    </section>
  );
}
